import React, { useEffect, useRef } from "react";

const toc = [
  ["Slab Setup", "slab-setup"],
  ["Intersection Cases", "intersection-cases"],
  ["Worked Example 1 — Hit", "example1"],
  ["Worked Example 2 — Miss", "example2"],
  ["Worked Example 3 — One Step", "example3"],
  ["Worked Example 4 — t₁ < t₀", "example4"],
  ["Quake II Analysis", "quake2"],
  ["Quake II Box Case", "quake2-box"],
];

const F = ({ children }) => (
  <div className="formula-block">
    {(Array.isArray(children) ? children : [children]).map((line, i) => (
      <div key={i} className="formula-row">{line}</div>
    ))}
  </div>
);

const RaySlab = ({ renderToc }) => {
  const ref = useRef(null);

  useEffect(() => {
    const tryRender = () => {
      if (window.renderMathInElement && ref.current) {
        window.renderMathInElement(ref.current, {
          delimiters: [
            { left: "$$", right: "$$", display: true },
            { left: "$", right: "$", display: false },
          ],
          throwOnError: false,
        });
      } else {
        setTimeout(tryRender, 100);
      }
    };
    tryRender();
  }, []);

  useEffect(() => {
    if (renderToc) renderToc(toc);
  }, [renderToc]);

  return (
    <div ref={ref}>
      <h1>Ray Slab — In Depth</h1>
      <p className="subtitle">
        by Jeff Wilson, Ph.D. &nbsp;·&nbsp; A deep dive on the AABB ray-slabs
        method and the Quake II implementation
      </p>

      {/* ── SLAB SETUP ── */}
      <h2 id="slab-setup">Ray–Polygon / Slab Setup</h2>
      <p>
        An <strong>AABB slab</strong> is a pair of parallel, axis-aligned
        planes. The full intersection test checks whether the ray's intervals
        across all three axis slabs simultaneously overlap.
      </p>
      <p>
        A key advantage: {"$t_\\text{near}$"} and {"$t_\\text{far}$"} for each
        axis are computed in <em>one step</em> rather than plane-by-plane.
      </p>

      {/* ── INTERSECTION CASES ── */}
      <h2 id="intersection-cases">Ray–Polygon Intersection Cases</h2>
      <p>
        For a single slab on one axis, given interval{" "}
        {"$[t_0,\\,t_1]$"}:
      </p>
      <ul>
        <li>
          <strong>Hit:</strong> {"$t_0 < t_1$"}
        </li>
        <li>
          <strong>Ray starts inside slab:</strong> {"$t_0<0<t_1$"} — an
          intersection (context-dependent)
        </li>
        <li>
          <strong>Ray starts past slab:</strong> both {"$t_0,t_1<0$"} — miss
        </li>
        <li>
          <strong>Miss:</strong> {"$t_0\\ge t_1$"}
        </li>
      </ul>
      <p>
        When {"$t_0=t_1$"} the ray hits a vertex or edge exactly. Whether this
        counts as an intersection is application-dependent.
      </p>

      {/* ── EXAMPLE 1 ── */}
      <h2 id="example1">Worked Example 1 — Basic Hit</h2>
      <p>
        Start state: {"$t_0=-\\infty,\\;t_1=+\\infty$"}. Process each axis in
        turn, narrowing the interval. After all three axes the interval is
        non-empty — hit confirmed.
      </p>

      {/* ── EXAMPLE 2 ── */}
      <h2 id="example2">Worked Example 2 — Miss</h2>
      <p>
        After processing all axes the interval becomes empty ({"$t_0>t_1$"})
        — miss.
      </p>

      {/* ── EXAMPLE 3 ── */}
      <h2 id="example3">Worked Example 3 — One-Step Computation</h2>
      <p>
        Demonstrates that {"$t_\\text{near}$"} and {"$t_\\text{far}$"} are
        resolved simultaneously per axis rather than one plane at a time.
      </p>

      {/* ── EXAMPLE 4 ── */}
      <h2 id="example4">
        Worked Example 4 — {"$t_1 < t_0$"}
      </h2>
      <p>
        After updating, {"$t_1<t_0$"} — no intersection.
      </p>

      {/* ── QUAKE II ── */}
      <h2 id="quake2">Quake II Source Analysis</h2>
      <p>
        The Quake II engine's collision code is a real-world slab
        implementation. Key variables:
      </p>
      <ul>
        <li>
          <code>enterfrac</code> — tracks {"$t_0$"}, initialised to{" "}
          {"$-1$"}
        </li>
        <li>
          <code>leavefrac</code> — tracks {"$t_1$"}, initialised to{"$+1$"}
        </li>
        <li>
          <code>clipplane</code> — the last plane that updated{" "}
          <code>enterfrac</code> (used to retrieve the surface normal on hit)
        </li>
      </ul>
      <pre>{`enterfrac = -1;\nleavefrac  =  1;\nclipplane  = NULL;`}</pre>
      <p>For each bounding plane, the {"$t$"} of intersection is:</p>
      <F>{"$$t=\\frac{d-\\mathbf{N}\\cdot\\mathbf{start}}{D}$$"}</F>
      <p>
        <code>enterfrac</code> updates when {"$t>\\text{enterfrac}$"};{" "}
        <code>leavefrac</code> when {"$t<\\text{leavefrac}$"}. If at any point{" "}
        <code>enterfrac</code> {"$\\ge$"} <code>leavefrac</code>, the ray
        misses.
      </p>

      {/* ── QUAKE II BOX CASE ── */}
      <h2 id="quake2-box">Quake II — General Box Case</h2>
      <p>
        For an AABB mover, Quake II expands each bounding plane outward by the
        half-extents of the moving box before running the slab test. This
        converts a box-vs-AABB test into a point-vs-expanded-AABB test.
      </p>
      <p>
        When the moving object is a point (zero extents), no plane adjustment
        is needed — the standard slab test applies directly.
      </p>

      <div className="note-box" style={{ marginTop: "2rem" }}>
        <div className="note-label">Core Insight</div>
        The AABB slab algorithm reduces a 3D box intersection to three 1D
        interval overlap tests. Hit iff{" "}
        {"$t_\\text{near}\\le t_\\text{far}$"}. Quake II extends this to swept-box
        collision by expanding the AABB before testing.
      </div>
    </div>
  );
};

export default RaySlab;
